/**
 * Permission Matrix Component — Phase 4 Redesign
 * Grouped card layout with collapsible sections, switch toggles, and active counts
 */
import React, { useState, useMemo } from 'react';
import { ChevronRight, Shield } from 'lucide-react';
import './PermissionMatrix.css';

// RBAC-WS-SEARCH-002: processor-workspace grants are additionally constrained for QC Specialists
const WORKSPACE_HINTS = {
  title_search: 'Processor workspace grant. For QC Specialists it is effective only when their QC Product Scope (Admin → Users) includes Title.',
  tax_search: 'Processor workspace grant. For QC Specialists it is effective only when their QC Product Scope (Admin → Users) includes Tax.',
};

const RESOURCE_LABELS = {
  analytics: 'Analytics',
  tax_search: 'Tax Search',
  title_search: 'Title Search',
  attendance: 'Attendance',
  audit: 'Audit Logs',
  flags: 'Flags & Markers',
  hr_records: 'HR Records',
  it_tasks: 'IT Tasks',
  it_tickets: 'IT Tickets',
  jurisdictions: 'Jurisdictions',
  leave: 'Leave Management',
  master_data: 'Master Data',
  orders: 'Orders',
  recruitment: 'Recruitment',
  sessions: 'Sessions',
  products: 'Products',
  rbac: 'Access Control',
  reports: 'Reports',
  roles: 'Role Management',
  settings: 'System Settings',
  templates: 'Templates',
  users: 'User Management',
};

const ACTION_LABELS = {
  view: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
  export: 'Export',
  approve: 'Approve',
  impersonate: 'Impersonate',
  lock: 'Lock',
  unlock: 'Unlock',
  reset_password: 'Reset Password',
  view_audit: 'View Audit',
  manage: 'Manage',
  assign: 'Assign',
  cancel: 'Cancel',
  restore: 'Restore',
  bulk_upload: 'Bulk Upload',
  investigate: 'Investigate',
};

const PermissionMatrix = ({
  allPermissions = [],
  activePerms = new Set(),
  onToggle,
  onToggleGroup,
  readOnly = false
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const groups = useMemo(() => {
    const map = {};
    for (const perm of allPermissions) {
      if (!map[perm.resource]) {
        map[perm.resource] = [];
      }
      map[perm.resource].push(perm);
    }
    return Object.entries(map)
      .map(([resource, perms]) => {
        const sorted = [...perms].sort((a, b) => {
          const order = ['view', 'create', 'update', 'delete', 'approve', 'export', 'assign', 'manage'];
          const ai = order.indexOf(a.action);
          const bi = order.indexOf(b.action);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
        return {
          resource,
          label: RESOURCE_LABELS[resource] || resource.charAt(0).toUpperCase() + resource.slice(1).replace(/_/g, ' '),
          actions: sorted,
          activeCount: sorted.filter(p => activePerms.has(`${p.resource}:${p.action}`)).length,
          totalCount: sorted.length
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allPermissions, activePerms]);

  const toggleGroupCollapse = (resource) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(resource)) next.delete(resource);
      else next.add(resource);
      return next;
    });
  };

  const totalActive = groups.reduce((sum, g) => sum + g.activeCount, 0);

  if (allPermissions.length === 0) {
    return (
      <div className="pm-empty" data-testid="permission-matrix-empty">
        <p>No permissions available</p>
      </div>
    );
  }

  return (
    <div className="pm-matrix" data-testid="permission-matrix">
      {readOnly && (
        <div className="pm-readonly-banner" data-testid="permission-matrix-readonly">
          <Shield className="w-4 h-4" />
          <span>View-only mode — you don't have permission to edit</span>
        </div>
      )}

      <div className="pm-groups">
        {groups.map(group => {
          const isCollapsed = collapsedGroups.has(group.resource);
          const allSelected = group.activeCount === group.totalCount;
          const noneSelected = group.activeCount === 0;

          return (
            <div
              key={group.resource}
              className={`pm-group ${isCollapsed ? 'collapsed' : ''}`}
              data-testid={`perm-group-${group.resource}`}
            >
              <div
                className="pm-group-header"
                onClick={() => toggleGroupCollapse(group.resource)}
              >
                <ChevronRight
                  className={`pm-chevron ${!isCollapsed ? 'expanded' : ''}`}
                  size={16}
                />
                <span className="pm-group-label">{group.label}</span>
                <span
                  className={`pm-group-count ${
                    allSelected ? 'all' : noneSelected ? 'none' : 'partial'
                  }`}
                  data-testid={`perm-group-count-${group.resource}`}
                >
                  {group.activeCount} of {group.totalCount} enabled
                </span>
                {!readOnly && (
                  <div className="pm-group-bulk" onClick={e => e.stopPropagation()}>
                    <button
                      className={`pm-bulk-btn ${allSelected ? 'disabled' : ''}`}
                      onClick={() => onToggleGroup(group.resource, true)}
                      disabled={allSelected}
                      data-testid={`perm-group-all-${group.resource}`}
                    >
                      All
                    </button>
                    <button
                      className={`pm-bulk-btn ${noneSelected ? 'disabled' : ''}`}
                      onClick={() => onToggleGroup(group.resource, false)}
                      disabled={noneSelected}
                      data-testid={`perm-group-none-${group.resource}`}
                    >
                      None
                    </button>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <div className="pm-group-body">
                  {WORKSPACE_HINTS[group.resource] && (
                    <p className="pm-group-hint" data-testid={`perm-group-hint-${group.resource}`}>
                      {WORKSPACE_HINTS[group.resource]}
                    </p>
                  )}
                  {group.actions.map(perm => {
                    const key = `${perm.resource}:${perm.action}`;
                    const isActive = activePerms.has(key);

                    return (
                      <label
                        key={key}
                        className={`pm-toggle ${isActive ? 'active' : ''} ${readOnly ? 'readonly' : ''}`}
                        data-testid={`perm-toggle-${key}`}
                      >
                        <div className="pm-switch">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => !readOnly && onToggle(perm.resource, perm.action)}
                            disabled={readOnly}
                          />
                          <span className="pm-switch-track">
                            <span className="pm-switch-thumb" />
                          </span>
                        </div>
                        <span className="pm-action-label">
                          {ACTION_LABELS[perm.action] || perm.action.replace(/_/g, ' ')}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pm-summary" data-testid="permission-matrix-summary">
        <div className="pm-summary-bar">
          <div
            className="pm-summary-fill"
            style={{ width: `${allPermissions.length > 0 ? (totalActive / allPermissions.length) * 100 : 0}%` }}
          />
        </div>
        <span className="pm-summary-text">
          {totalActive} of {allPermissions.length} permissions active
        </span>
      </div>
    </div>
  );
};

export default PermissionMatrix;
